import { app } from '../../core/App';
import { Sounds } from '../../sounds';
import { SoundBanks } from '../../sound-banks';
import CharacterSpine, { VehicleSkins, VehicleSkinsValues } from './CharacterSpine';
import { BodyTypes } from '../LevelManager';
import { RoadDetails } from '../level/themes/theme';
import { LevelUtil } from '../level/levels/LevelUtil';
import { GameDataHandler } from '../GameDataHandler';
import { VehicleManager } from './VehicleManager';
import { HitEffect, HitEffectValues } from '../level/LaneEntityImageBase';
import { CharacterNameValues, CharacterName, Character, VehicleShadow } from './Character';
import { RampGuide } from '../level/RampGuide';
import { EquipmentType, EquipmentTypeValues, VehicleEquipment } from './VehicleEquipment';
import { VehicleEquipmentMeleeWeapon } from './VehicleEquipmentMeleeWeapon';
import { ObstacleSpine } from '../level/entities/obstacle/ObstacleSpine';
import { RampFeature } from './../level/entities/RampFeature';
import { RingFeature } from './../level/entities/RingFeature';

export const Driver = {
  Villain: 'villain',
  Hero: 'hero'
};
export type DriverKeys = keyof typeof Driver;
export type DriverValues = typeof Driver[DriverKeys];

export interface DriverProfile {
  driver: CharacterNameValues;
  driverTeam: DriverValues;
  skin: VehicleSkinsValues;
  equipment: EquipmentTypeValues[];
}

export interface VehicleHandling {
  // X
  accelerationXPerMS: number;
  decelerationXPerMS: number;
  topSpeedXPerMS: number;
  topBoostSpeedXPerMS: number;
  maxSpeedXPerMS: number;

  // Y
  accelerationYPerMS: number;
  decelerationYPerMS: number;
  topSpeedYPerMS: number;

  boostSpeedDecayRatePerMS: number;
}

export interface VehicleDetails {
  vehicleLayer: Phaser.GameObjects.Layer;
  gameUILayer: Phaser.GameObjects.Layer;
  startLane: number;
  startIndentX: number;
  endPointX: number;
  hasShadow: boolean;
}

export interface VehicleBounds {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

export interface VehicleSounds {
  engine: string;
  engineRef: string;
  boost: string;
  punch: string;
  hit: string;
  prefire: string;
  fire: string;
  cheer: string;
  whoa: string;
  villainVoice: string;
}

export class Vehicle extends window.SpinePlugin.SpineContainer {
  public static skinsToSize: Map<VehicleSkinsValues, VehicleBounds> = new Map([
    [VehicleSkins.Bam, { x: 0, y: 5, width: 250, height: 60 }],
    [VehicleSkins.Prank, { x: 0, y: 40, width: 275, height: 60 }],
    [VehicleSkins.Bibi, { x: -6, y: -2, width: 244, height: 70 }],
    [VehicleSkins.Jestah, { x: -2, y: 76, width: 184, height: 60 }],
    [VehicleSkins.Batwing, { x: -2, y: 10, width: 164, height: 90 }],
    [VehicleSkins.Quizz, { x: -2, y: 76, width: 184, height: 60 }],
    [VehicleSkins.Redbird, { x: -2, y: 10, width: 220, height: 80 }],
    [VehicleSkins.Ducky, { x: -2, y: 50, width: 220, height: 100 }],
    [VehicleSkins.Buff, { x: -2, y: 50, width: 220, height: 100 }],
    [VehicleSkins.Snowy, { x: -2, y: 50, width: 220, height: 100 }]
  ]);

  public static skinsOffsets: Map<VehicleSkinsValues, number> = new Map([
    [VehicleSkins.Bam, 0],
    [VehicleSkins.Prank, 0],
    [VehicleSkins.Bibi, 0],
    [VehicleSkins.Jestah, 0],
    [VehicleSkins.Batwing, 0],
    [VehicleSkins.Quizz, 0],
    [VehicleSkins.Redbird, -10],
    [VehicleSkins.Ducky, -35],
    [VehicleSkins.Buff, -5],
    [VehicleSkins.Snowy, -20]
  ]);

  public static skinToShadowLookup: Record<VehicleSkinsValues, VehicleShadow> = {
    [VehicleSkins.Bam]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -5 },
    [VehicleSkins.Prank]: { key: 'game', frame: 'prank-shadow.png', offsetX: -35, offsetY: 3 },
    [VehicleSkins.Bibi]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -5 },
    [VehicleSkins.Jestah]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -5 },
    [VehicleSkins.Batwing]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -5 },
    [VehicleSkins.Quizz]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -5 },
    [VehicleSkins.Redbird]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -15 },
    [VehicleSkins.Ducky]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -15 },
    [VehicleSkins.Buff]: { key: 'game', frame: 'bam-shadow.png', offsetX: -25, offsetY: -5 },
    [VehicleSkins.Snowy]: { key: 'game', frame: 'bam-shadow.png', offsetX: -35, offsetY: -5 }
  };

  public static events = {
    RingEnter: 'ringEnter',
    RingExit: 'ringExit'
  } as const;

  protected vehicleManager: VehicleManager;
  protected driverProfile: DriverProfile;
  protected vehicleHandlingProfile: VehicleHandling;
  protected roadDetails: RoadDetails;
  protected vehicleDetails: VehicleDetails;
  protected vehicleLayer: Phaser.GameObjects.Layer;
  protected character: CharacterSpine;

  protected vehicleBody: MatterJS.BodyType | undefined;
  public sounds: VehicleSounds;

  protected pointsScored: number;
  protected isLocked: boolean;
  protected isMoving: boolean;
  protected shouldControlX: boolean;
  protected shouldControlY: boolean;
  protected shouldControlDepth: boolean;
  protected isStuckInTraffic: boolean;
  protected isBlockedByObstacles: boolean;
  protected isAccelerating: boolean;
  protected isMovingUp: boolean;
  protected isMovingDown: boolean;
  protected isJumping: boolean;
  protected isInTheAir: boolean;
  protected isLanding: boolean;
  protected inRing: boolean;
  protected isOffScreen: boolean;
  protected hasMovedYOnce: boolean;
  protected hasTargetY: boolean;

  protected objectToTrack: object | undefined;
  protected trackObjectX: boolean;
  protected trackObjectY: boolean;
  protected trackTargetOffsetX: number;
  protected trackCurrentOffsetX: number;

  // X Movement
  protected accelerationXPerMS: number;
  protected decelerationXPerMS: number;
  protected normalSpeed: number;
  protected boostSpeed: number;
  protected bonusSpeed: number;
  protected topSpeedXPerMS: number;
  protected topBoostSpeedXPerMS: number;
  protected prevMoveSpeedX: number;
  protected prevMoveSpeedXPerMS: number;

  // Y Movement
  protected roadY: number;
  protected accelerationYPerMS: number;
  protected decelerationYPerMS: number;
  protected speedY: number;
  protected topSpeedYPerMS: number;
  protected targetY: number;

  // Movement
  protected drag: number;
  protected speedLimitPerMS: number;
  protected maxSpeedPerMS: number;
  protected minSpeedPerMS: number;
  protected boostSpeedDecayRatePerMS: number;
  protected safeExtraFinishDistance: number;

  // Swerving
  protected areControlsInverted: boolean;
  protected isOutOfControl: boolean;
  protected outOfControlDuration: number;
  protected outOfControlTimer: number;
  protected swervePoints: number[];
  protected swervePointIndex: number;

  // Panning
  protected isPanningByTime: boolean;
  protected isPanningBySpeed: boolean;
  protected panDuration: number;
  protected panElapsedTime: number;
  protected panSpeedXPerMS: number;
  protected panStartX: number;
  protected panOffsetX: number;
  protected prevPanOffsetX: number;
  protected panMovement: number;
  protected panTargetX: number;
  protected panEase: ((progress: number, ...rest: any) => number) | undefined;

  // jumping
  protected jumpDuration: number;
  protected jumpTimer: number;
  protected jumpOffsetY: number;
  protected jumpPrevOffsetY: number;

  protected vehicleBounds!: Phaser.Geom.Rectangle;

  // protected debugGraphics: Phaser.GameObjects.Graphics;

  protected shadowData: VehicleShadow;
  protected shadow: Phaser.GameObjects.Image;

  protected equipment: VehicleEquipment[];

  constructor(
    scene: Phaser.Scene,
    vehicleManager: VehicleManager,
    driverProfile: DriverProfile,
    vehicleHandling: VehicleHandling,
    roadDetails: RoadDetails,
    vehicleDetails: VehicleDetails
  ) {
    let [startX, startY] = LevelUtil.getVehicleStartPoint(vehicleDetails.startLane, vehicleDetails.startIndentX, roadDetails);
    super(scene, scene.sys.spine, startX, startY);

    this.vehicleManager = vehicleManager;
    this.driverProfile = driverProfile;
    this.vehicleHandlingProfile = vehicleHandling;
    this.roadDetails = roadDetails;
    this.vehicleDetails = vehicleDetails;
    this.vehicleLayer = this.vehicleDetails.vehicleLayer;

    let characterOffset = Vehicle.skinsOffsets.get(this.driverProfile.skin) ?? 0;
    this.character = new CharacterSpine(this.scene, 0, characterOffset, this.driverProfile.skin);
    this.character.addToUpdateList();
    this.add(this.character);
    this.character.refresh();

    this.setScale(0.9);

    this.sounds = SoundBanks.buildVehicleSounds(Character.characterToSoundLookup[this.CurrentCharacter]);

    this.pointsScored = 0;
    this.isLocked = false;
    this.isMoving = false;
    this.shouldControlX = true;
    this.shouldControlY = true;
    this.shouldControlDepth = true;
    this.isStuckInTraffic = false;
    this.isBlockedByObstacles = false;
    this.isAccelerating = false;
    this.isMovingUp = false;
    this.isMovingDown = false;
    this.isJumping = false;
    this.isInTheAir = false;
    this.isLanding = false;
    this.inRing = false;
    this.isOffScreen = false;
    this.hasMovedYOnce = false;
    this.hasTargetY = false;

    this.trackObjectX = false;
    this.trackObjectY = false;
    this.trackTargetOffsetX = 0;
    this.trackCurrentOffsetX = 0;

    // X Movement
    this.accelerationXPerMS = this.vehicleHandlingProfile.accelerationXPerMS;
    this.decelerationXPerMS = this.vehicleHandlingProfile.decelerationXPerMS;
    this.normalSpeed = this.vehicleHandlingProfile.topSpeedXPerMS * 16.66; // Start at roughly max speed.
    this.boostSpeed = 0;
    this.bonusSpeed = 0;
    this.topSpeedXPerMS = this.vehicleHandlingProfile.topSpeedXPerMS;
    this.topBoostSpeedXPerMS = this.vehicleHandlingProfile.topBoostSpeedXPerMS;
    this.prevMoveSpeedX = 0;
    this.prevMoveSpeedXPerMS = 0;

    // Y Movement
    this.roadY = startY;
    this.accelerationYPerMS = this.vehicleHandlingProfile.accelerationYPerMS;
    this.decelerationYPerMS = this.vehicleHandlingProfile.decelerationYPerMS;
    this.speedY = 0;
    this.topSpeedYPerMS = this.vehicleHandlingProfile.topSpeedYPerMS;
    this.targetY = startY;

    // Movement
    this.drag = 0;
    this.speedLimitPerMS = this.vehicleHandlingProfile.maxSpeedXPerMS;
    this.maxSpeedPerMS = this.vehicleHandlingProfile.maxSpeedXPerMS;
    this.minSpeedPerMS = 0.05;
    this.boostSpeedDecayRatePerMS = this.vehicleHandlingProfile.boostSpeedDecayRatePerMS;
    this.safeExtraFinishDistance = 500;

    // Swerving
    this.areControlsInverted = false;
    this.isOutOfControl = false;
    this.outOfControlDuration = 0;
    this.outOfControlTimer = 0;
    this.swervePoints = [];
    this.swervePointIndex = 0;

    // Panning
    this.isPanningByTime = false;
    this.isPanningBySpeed = false;
    this.panDuration = 0;
    this.panElapsedTime = 0;
    this.panSpeedXPerMS = 0.3125;
    this.panStartX = 0;
    this.panOffsetX = 0;
    this.prevPanOffsetX = 0;
    this.panMovement = 0;
    this.panTargetX = 0;

    this.jumpDuration = 1000;
    this.jumpTimer = this.jumpDuration;
    this.jumpOffsetY = 0;
    this.jumpPrevOffsetY = 0;

    // Setup shadow.
    this.shadowData = Vehicle.skinToShadowLookup[this.driverProfile.skin];
    this.shadow = new Phaser.GameObjects.Image(this.scene, this.shadowData.offsetX, this.shadowData.offsetY, this.shadowData.key, this.shadowData.frame);
    this.shadow.setDepth(startY - 1);
    if (this.vehicleDetails.hasShadow) this.vehicleLayer.add(this.shadow);

    this.equipment = [];

    this.injectMatter(this);

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
      this.stopEngineSFX();
      console.log('Destroy vehicle.', this.CurrentSkin);
    });

    // this.debugGraphics = this.scene.add.graphics();
  }

  get VehicleManager(): VehicleManager {
    return this.vehicleManager;
  }

  get IsPlayer(): boolean {
    return this.driverProfile.driverTeam == Driver.Hero;
  }

  get IsVillain(): boolean {
    return this.driverProfile.driverTeam == Driver.Villain;
  }

  get IsTaxi(): boolean {
    return this.driverProfile.driverTeam == Driver.Hero || this.driverProfile.driverTeam == Driver.Villain;
  }

  get IsMoving(): boolean {
    return this.isMoving;
  }

  get IsPanning(): boolean {
    return this.isPanningByTime || this.isPanningBySpeed;
  }

  get IsAccelerating(): boolean {
    return this.isAccelerating;
  }

  get IsAirborne(): boolean {
    return this.isJumping || this.isInTheAir || this.isLanding || this.inRing;
  }

  get HasFinishedRun(): boolean {
    return this.isOffScreen;
  }

  get CharacterSpine(): CharacterSpine {
    return this.character;
  }

  get CurrentCharacter(): CharacterNameValues {
    return this.driverProfile.driver;
  }

  get CurrentSkin(): VehicleSkinsValues {
    return this.character.CurrentSkin;
  }

  get DriverProfile(): DriverProfile {
    return this.driverProfile;
  }

  get RoadDetails(): RoadDetails {
    return this.roadDetails;
  }

  get VehicleDetails(): VehicleDetails {
    return this.vehicleDetails;
  }

  get VehicleBody(): MatterJS.BodyType | undefined {
    return this.vehicleBody;
  }

  get VehicleBounds(): Phaser.Geom.Rectangle {
    return this.vehicleBounds;
  }

  get HasMovedYOnce(): boolean {
    return this.hasMovedYOnce;
  }

  get CurrentSpeedX(): number {
    return this.prevMoveSpeedX;
  }

  get CurrentSpeedXPerMS(): number {
    return this.prevMoveSpeedXPerMS;
  }

  public setupVehicleHandling(vehicleHandling: VehicleHandling) {
    this.vehicleHandlingProfile = vehicleHandling;

    // X
    this.accelerationXPerMS = this.vehicleHandlingProfile.accelerationXPerMS;
    this.decelerationXPerMS = this.vehicleHandlingProfile.decelerationXPerMS;
    this.topSpeedXPerMS = this.vehicleHandlingProfile.topSpeedXPerMS;
    this.topBoostSpeedXPerMS = this.vehicleHandlingProfile.topBoostSpeedXPerMS;
    this.speedLimitPerMS = this.vehicleHandlingProfile.maxSpeedXPerMS;
    this.maxSpeedPerMS = this.vehicleHandlingProfile.maxSpeedXPerMS;

    // Y
    this.accelerationYPerMS = this.vehicleHandlingProfile.accelerationYPerMS;
    this.decelerationYPerMS = this.vehicleHandlingProfile.decelerationYPerMS;
    this.topSpeedYPerMS = this.vehicleHandlingProfile.topSpeedYPerMS;

    this.boostSpeedDecayRatePerMS = this.vehicleHandlingProfile.boostSpeedDecayRatePerMS;
  }

  public toggleMovementLock(isLocked: boolean) {
    this.isLocked = isLocked;
  }

  public resetPosition(x: number, y: number) {
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: x, y: y });
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
  }

  public shiftPosition(x: number, y: number) {
    console.log(`Shifting player from ${this.x}, ${this.y} to ${x}, ${y}`);
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: x, y: y });
  }

  public startMoving() {
    console.log('Moving');
    this.isMoving = true;
    this.character.startMoving();
    this.isLocked = false;
    this.startEngineSFX(this.sounds.engine);
  }

  public stopMoving() {
    console.log('Stopping');
    this.isMoving = false;
    this.character.stopMoving();
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    this.isLocked = true;
    this.stopEngineSFX();

    // this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
  }

  public freeze() {
    this.isMoving = false;
    this.character.stopMoving();
    this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: 0, y: 0 });
    this.scene.matter.body.setStatic(this.body as MatterJS.BodyType, true);
    this.isLocked = true;
  }

  public runFinished() {
    // this.startMoving();
    // this.accelerateX();
    this.isLocked = true;
    this.stopEngineSFX();
  }

  public cleanUp() {
    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, { x: this.x + this.safeExtraFinishDistance + 1000, y: this.y });
    this.stopEngineSFX();
  }

  public teleportToPositionInLane(x: number, y: number) {
    this.setPosition(x, y);
    this.setDepth(y);
  }

  public switchSkin(skin: VehicleSkinsValues) {
    this.character.switchSkin(skin);
    this.driverProfile.skin = skin;
  }

  public setTrackingTarget(target: object) {
    this.objectToTrack = target;
  }

  public toggleTrackingX(shouldTrack: boolean) {
    this.trackObjectX = shouldTrack;
  }

  public toggleTrackingY(shouldTrack: boolean) {
    this.trackObjectY = shouldTrack;
  }

  public toggleYControl(shouldControlY: boolean) {
    this.shouldControlY = shouldControlY;
  }

  public toggleDepthControl(shouldControlDepth: boolean) {
    this.shouldControlDepth = shouldControlDepth;
  }

  public accelerateX(shouldForce: boolean = false) {
    if (this.isLocked && !shouldForce) return;

    this.isAccelerating = true;
  }

  public decelerateX(shouldForce: boolean = false) {
    if (this.isLocked && !shouldForce) return;

    this.isAccelerating = false;
  }

  public moveUpRoad() {
    if (this.isLocked) return;

    this.hasMovedYOnce = true;
    this.isMovingUp = true;
  }

  public moveDownRoad() {
    if (this.isLocked) return;

    this.hasMovedYOnce = true;
    this.isMovingDown = true;
  }

  public moveToY(targetY: number, shouldForce: boolean = false) {
    if (this.isLocked && !shouldForce) return;

    this.hasMovedYOnce = true;
    this.hasTargetY = true;
    this.targetY = targetY;
  }

  // public tweenToY(targetY: number, duration: number, shouldForce: boolean = false) {
  //   if (this.isLocked && !shouldForce) return;

  //   this.hasMovedYOnce = true;
  //   this.shouldControlY = false;

  //   this.scene.add.tween({
  //     targets: this,
  //     y: targetY,
  //     duration: duration,
  //     ease: Phaser.Math.Easing.Cubic.InOut,
  //     onComplete: () => {
  //       this.shouldControlY = true;
  //       this.targetY = targetY;
  //     }
  //   });
  // }

  public jump(ramp: RampFeature) {
    this.isJumping = true;
    this.isLocked = true;

    this.moveToY(ramp.getRampEntry(), true);

    let shadowTween = this.scene.add.tween({
      targets: this.shadow,
      scaleX: 0.5,
      scaleY: 0.5,
      duration: 1000,
      ease: Phaser.Math.Easing.Linear
    });

    app.audioHandler.playSound(this.scene, this.sounds.boost, Sounds.Channel.SFX, { volume: 1.0 });

    this.CharacterSpine.once(CharacterSpine.events.RampInEnded, () => {
      this.isJumping = false;
      this.jumpTimer = this.jumpDuration;
      this.isInTheAir = true;

      this.jumpOffsetY = 0;
      this.jumpPrevOffsetY = 0;

      let jumpHeight = -160;

      let initialJump = -2;
      this.jumpOffsetY = initialJump;

      let upTween = this.scene.tweens.addCounter({
        from: 0,
        to: jumpHeight,
        duration: this.jumpDuration * 0.4,
        ease: Phaser.Math.Easing.Linear,
        onUpdate: (tween, target, param) => {
          this.jumpOffsetY = initialJump + upTween.getValue();
        },
        onComplete: () => {
          let shadowTween = this.scene.add.tween({
            targets: this.shadow,
            scaleX: 1,
            scaleY: 1,
            duration: 750,
            ease: Phaser.Math.Easing.Linear
          });
        }
      });

      let downTween = this.scene.tweens.addCounter({
        from: jumpHeight,
        to: 0,
        duration: this.jumpDuration * 0.6,
        ease: Phaser.Math.Easing.Quadratic.In,
        delay: this.jumpDuration * 0.4,
        onUpdate: (tween, target, param) => {
          this.jumpOffsetY = initialJump + downTween.getValue();
        },
        onComplete: () => {
          this.jumpOffsetY = 0;
          this.land();
        }
      });
    });

    this.CharacterSpine.rampIn(this.prevMoveSpeedXPerMS);
  }

  public land() {
    this.isInTheAir = false;
    this.isLanding = true;

    this.CharacterSpine.once(CharacterSpine.events.RampOutEnded, () => {
      this.isLocked = false;
      this.isLanding = false;
    });

    this.CharacterSpine.rampOut(this.prevMoveSpeedXPerMS);
  }

  public ring(ring: RingFeature) {
    this.inRing = true;
    this.isLocked = true;
    this.moveToY(ring.getRampEntry(), true);

    app.audioHandler.playSound(this.scene, this.sounds.boost, Sounds.Channel.SFX, { volume: 1.0 });

    this.emit(Vehicle.events.RingEnter);

    this.scene.time.delayedCall(
      500,
      () => {
        this.emit(Vehicle.events.RingExit);
        this.inRing = false;
        this.isLocked = false;
      },
      undefined,
      this
    );
  }

  public boost(boostSpeed: number) {
    if (this.IsTaxi && !this.isStuckInTraffic && !this.isBlockedByObstacles) {
      this.boostSpeed = Math.min(this.topBoostSpeedXPerMS, this.boostSpeed + boostSpeed);
      // this.character.boost();
      //this.setEngineSFX('SndEngine4');
      // app.audioHandler.playSound(this.scene, 'SndPickup', Sounds.channels.SFX);
      // if (this.vehicleDetails.profile.driver == Driver.PlayerTaxi) console.log('BoostSpeed', this.boostSpeed, this.topBoostSpeed);
    }
  }

  public slow(slowStrength: number, hitEffect: HitEffectValues) {
    if (this.driverProfile.driverTeam == Driver.Hero || this.driverProfile.driverTeam == Driver.Villain) {
      this.boostSpeed = 0;

      if (hitEffect == HitEffect.Impact) {
        // this.character.bounce();
        this.drag += 10 * slowStrength;
      } else if (hitEffect == HitEffect.Slide) {
        // this.character.slide();
        // app.audioHandler.playSound(this.scene, 'SndObstacleSkid', Sounds.channels.SFX);
        // this.randomLaneChange();
      }
    }
  }

  public onHitBackStart(weapon: VehicleEquipmentMeleeWeapon) {
    // Make so we can only hit once.
    if (this.isLocked) return;

    this.isLocked = true;
    this.targetY = LevelUtil.RoadMiddle;

    this.CharacterSpine.hitRecoil();

    app.audioHandler.playSound(this.scene, this.sounds.whoa, Sounds.Channel.SFX, { volume: 1 });
    app.audioHandler.playSound(this.scene, this.sounds.hit, Sounds.Channel.SFX, { volume: 1 });
    weapon.onHit();
    // app.audioHandler.playSound(this.scene, this.sounds.punch, Sounds.Channel.SFX, { volume: 1.0 });

    this.panByTimeToXRelativePointX(this.scene.cameras.main.worldView.centerX, -600, 350, Phaser.Math.Easing.Quadratic.In);

    this.scene.time.delayedCall(750, () => {
      this.swervePoints = [
        LevelUtil.RoadMiddle - 100,
        LevelUtil.RoadMiddle + 75,
        LevelUtil.RoadMiddle - 50,
        // LevelUtil.RoadMiddle - 25,
        // LevelUtil.RoadMiddle + 25,
        LevelUtil.RoadMiddle
      ];
      this.swervePointIndex = 0;

      this.isOutOfControl = true;
      this.outOfControlDuration = 3000;
      this.outOfControlTimer = this.outOfControlDuration;

      this.panByTimeToXRelativePointX(this.scene.cameras.main.worldView.centerX, -350, 2000, Phaser.Math.Easing.Back.Out);
      weapon.Owner.panByTimeToXRelativePointX(this.scene.cameras.main.worldView.centerX, 350, 500, Phaser.Math.Easing.Linear);
    });
  }

  public onSwerveStart() {
    // Make so we can only hit once.
    if (this.isLocked) return;

    this.isLocked = true;
    this.targetY = LevelUtil.RoadMiddle;

    this.swervePoints = [
      LevelUtil.RoadMiddle - 100,
      LevelUtil.RoadMiddle + 75,
      LevelUtil.RoadMiddle - 50,
      // LevelUtil.RoadMiddle - 25,
      // LevelUtil.RoadMiddle + 25,
      LevelUtil.RoadMiddle
    ];
    this.swervePointIndex = 0;

    this.isOutOfControl = true;
    this.outOfControlDuration = 3000;
    this.outOfControlTimer = this.outOfControlDuration;
  }

  public onOutOfControlEnd() {
    this.isLocked = false;
  }

  public panXToRelativePoint(relativeStartX: number, targetX: number) {
    this.panStartX = this.x - relativeStartX;
    this.panOffsetX = this.panStartX;
    this.prevPanOffsetX = this.panStartX;
    this.panTargetX = targetX;

    this.isPanningByTime = true;
  }

  public panByTimeToXRelativePointX(relativeStartX: number, targetX: number, duration: number, ease: (progress: number, ...rest: any) => number) {
    this.panDuration = duration;
    this.panElapsedTime = 0;
    this.panEase = ease;

    this.panStartX = this.x - relativeStartX;
    this.panOffsetX = this.panStartX;
    this.prevPanOffsetX = 0;
    this.panTargetX = targetX;

    this.isPanningByTime = true;

    console.log(`${this.CharacterSpine.CurrentSkin}`, this.panStartX, this.panTargetX);
  }

  public panBySpeedToRelativePointX(relativeStartX: number, targetX: number, speed?: number) {
    this.panStartX = this.x - relativeStartX;
    this.panOffsetX = this.panStartX;
    this.prevPanOffsetX = this.panStartX;
    this.panTargetX = targetX;

    this.isPanningBySpeed = true;

    console.log(`${this.CharacterSpine.CurrentSkin}`, this.panStartX, this.panTargetX);
  }

  // public pickupPassenger(passenger: Passenger) {
  //   console.log('Picked up passenger!');

  //   passenger.pickedUp();
  //   // this.taxiUI?.showScore(passenger.PickupBonus);
  //   this.addScore(passenger.PickupBonus);
  //   this.taxiUI?.addPassenger(passenger, false);
  //   this.passengers.push({
  //     passenger: passenger,
  //     rideStartX: Math.trunc(this.x),
  //     lastScoredX: Math.trunc(this.x),
  //     farePointsAccrued: 0,
  //     unstealable: this.passengerStealDefence
  //   });
  //   app.audioHandler.playSound(this.scene, 'SndPickup2', Sounds.channels.SFX);
  // }

  // public dropOffPassenger(passenger: Passenger): boolean {
  //   console.log('Drop off passenger!');

  //   for (let i = this.passengers.length - 1; i >= 0; i--) {
  //     let taxiPassenger = this.passengers[i];

  //     if (taxiPassenger.passenger == passenger) {
  //       // this.taxiUI?.showScore(passenger.DropOffBonus);
  //       this.addScore(passenger.DropOffBonus);
  //       this.taxiUI?.removePassenger(passenger);
  //       this.passengers.splice(i, 1);
  //       app.audioHandler.playSound(this.scene, 'SndPickup2', Sounds.channels.SFX);
  //       return true;
  //     }
  //   }

  //   return false;
  // }

  // public findPassengerToSteal(): PickedUpPassenger | undefined {
  //   if (this.passengers.length <= 0) return;
  //   for (let i = this.passengers.length - 1; i >= 0; i--) {
  //     const pickedUpPassenger = this.passengers[i];
  //     if (pickedUpPassenger.unstealable <= 0) {
  //       return pickedUpPassenger;
  //     }
  //   }
  //   return;
  // }

  // public startBeingStolen(stealingVehicle: Vehicle, passenger: Passenger) {
  //   this.stealUI?.showBeingStolen();
  // }

  // public updateBeingStolen(stealingVehicle: Vehicle, stealProgress: number, passenger: Passenger) {
  //   this.taxiUI!.updateBeingStolen(stealingVehicle, stealProgress, passenger);
  //   this.stealUI?.updateBeingStolen(stealProgress / 100);
  // }

  // public stopBeingStolen(passenger: Passenger) {
  //   this.taxiUI?.stopBeingStolen(passenger);
  //   this.stealUI?.hideBeingStolen();
  // }

  // public stealPassengerFrom(targetVehicle: Vehicle, passenger: Passenger) {
  //   console.log('Passenger Stolen!!');
  //   app.audioHandler.playSound(this.scene, 'SndSteal2', Sounds.channels.SFX);
  //   targetVehicle.loosePassengerTo(this, passenger);
  //   // targetVehicle.stopBeingStolen(this, passenger);
  //   targetVehicle.stopBeingStolen(passenger);
  //   this.taxiUI?.updatePassenger(passenger, 1);
  //   this.stealUI?.hideStealingFrom();
  //   this.showOutOfRangeOfStealingWithTarget(targetVehicle);
  // }

  // public loosePassengerTo(otherTaxi: Vehicle, passengerToGet: Passenger) {
  //   let pickedUpPassenger = this.passengers.pop();
  //   if (!pickedUpPassenger) return;

  //   this.taxiUI?.removePassenger(pickedUpPassenger.passenger);
  //   otherTaxi.gainPassengerFrom(pickedUpPassenger.passenger, this);
  // }

  // public gainPassengerFrom(passenger: Passenger, otherTaxi: Vehicle) {
  //   console.log('Gained passenger!');
  //   // this.taxiUI?.addPassenger(passenger);
  //   this.passengers.push({
  //     passenger: passenger,
  //     rideStartX: Math.trunc(this.x),
  //     lastScoredX: Math.trunc(this.x),
  //     farePointsAccrued: 0,
  //     unstealable: this.passengerStealDefence
  //   });
  // }

  // public showOutOfRangeOfStealingWithTarget(vehicle: Vehicle) {
  //   if (this.inRangeOfStealingWithTarget.has(vehicle)) {
  //     vehicle.showOutRangeOfBeingStolen();
  //     this.inRangeOfStealingWithTarget.delete(vehicle);
  //   }
  // }

  // public showOutRangeOfBeingStolen() {
  //   this.inRangeOfBeingStolen--;
  // }

  public update(time: number, delta: number) {
    // If we're off the map we can skip updating.
    if (this.isOffScreen) return;

    // If we're a safe way past the edge of the map then we've finished the run.
    // if (this.x > this.vehicleDetails.endPointX + this.safeExtraFinishDistance) {
    //   this.isOffScreen = true;
    //   this.cleanUp();
    //   return;
    // }

    // for (let i = 0; i < this.passengers.length; i++) {
    //   const pickedUpPassenger = this.passengers[i];
    //   if (pickedUpPassenger.unstealable > 0) {
    //     pickedUpPassenger.unstealable -= delta;
    //     if (pickedUpPassenger.unstealable < 0) {
    //       pickedUpPassenger.unstealable = 0;
    //     }
    //   }

    //   let distanceToScore = Math.trunc(this.x) - pickedUpPassenger.lastScoredX;
    //   if (distanceToScore > 0) {
    //     let pointsToScore = distanceToScore * pickedUpPassenger.passenger.DistanceRate;
    //     pickedUpPassenger.lastScoredX = Math.trunc(this.x);
    //     pickedUpPassenger.farePointsAccrued += pointsToScore;

    //     this.addScore(pointsToScore);
    //     this.addDistance(distanceToScore);
    //   }
    // }

    // if (!this.isInRangeWithTarget && (this.inRangeOfStealingWithTarget.size > 0 || this.inRangeOfBeingStolen > 0)) {
    //   this.stealUI?.showInRange(true);
    //   this.isInRangeWithTarget = true;
    // } else if (!this.isInRange && !this.isInRangeWithTarget && this.inRangeOfStealing.size > 0) {
    //   this.stealUI?.showInRange(false);
    //   this.isInRange = true;
    // } else if (this.isInRangeWithTarget && this.inRangeOfStealingWithTarget.size <= 0 && this.inRangeOfBeingStolen <= 0) {
    //   this.stealUI?.showOutOfRange();
    //   this.isInRangeWithTarget = false;
    // } else if (this.isInRange && this.inRangeOfStealing.size <= 0) {
    //   this.stealUI?.showOutOfRange();
    //   this.isInRange = false;
    // }

    // Civilian behaviour.
    // if (this.IsTraffic && (this.isBlockingTraffic || this.isBlockedByObstacles) && !this.IsChangingLane) {
    //   this.randomLaneChange();
    // }

    // if (this.isInTheAir) {
    //   this.jumpTimer -= delta;

    //   if (this.jumpTimer > this.jumpDuration * 0.5) this.jumpOffsetY -= 10;
    //   else this.jumpOffsetY += 10;

    //   if (this.jumpTimer <= 0) {
    //     this.jumpOffsetY = 0;
    //     this.land();
    //   }
    // }

    if (this.isPanningByTime) this.panByTime(time, delta);
    else if (this.isPanningBySpeed) this.panBySpeed(time, delta);

    if (this.isMoving) this.calculateSpeed(time, delta);

    if (this.IsPlayer) {
      // let weaponBone = this.character.findBone('bone4');
      // console.log(this.character.getAttachment(1, "weapon_arm"));
      // console.log(this.character.findBone('weapon').worldX);
      // this.weaponBoxDebug.x = weaponBone.worldX + this.scene.cameras.main.scrollX;
      // this.weaponBoxDebug.y = -weaponBone.worldY - this.scene.cameras.main.scrollY;
      // console.log("bone", weaponBone.rotation, weaponBone.arotation, weaponBone.getWorldRotationX(), weaponBone.getWorldRotationY());
      // weaponBone.rotation += this.prevMoveSpeedX;
      // weaponBone.updateWorldTransform();
    }
  }

  public matterUpdate(time: number, delta: number) {
    // If we're off the map we can skip updating.
    if (this.isOffScreen) return;

    // if (this.boostSpeed > 0) {
    //   this.boostSpeed = Math.max(0, this.boostSpeed - this.boostSpeedDecayRatePerMS);
    //   if (this.boostSpeed <= 0) {
    //     this.setEngineSFX('SndEngine1');
    //     this.boostSpeed = 0;
    //   }
    // } else if (this.boostSpeed < 0) {
    //   this.boostSpeed = 0;

    //   // if (this.vehicleDetails.profile.driver == Driver.PlayerTaxi) console.log('Boost Over!', this.boostSpeed, this.topBoostSpeed);
    // }
  }

  protected panByTime(time: number, delta: number) {
    this.panElapsedTime += delta;

    let progress = Phaser.Math.Clamp(this.panElapsedTime / this.panDuration, 0, 1);
    progress = this?.panEase?.(progress) ?? progress;

    if (this.panElapsedTime < this.panDuration) {
      this.panOffsetX = (this.panTargetX - this.panStartX) * progress;
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;
    } else {
      this.panOffsetX = this.panTargetX - this.panStartX;
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;

      this.isPanningByTime = false;
    }
  }

  protected panBySpeed(time: number, delta: number) {
    let panSpeedX = this.panSpeedXPerMS * delta;
    let distanceToTarget = this.panTargetX - this.panOffsetX;
    let distanceToTargetAbs = Math.abs(distanceToTarget);

    if (distanceToTargetAbs > panSpeedX) {
      let progress = panSpeedX / distanceToTargetAbs;

      this.panOffsetX = Phaser.Math.Interpolation.Bezier([this.panOffsetX, this.panTargetX], progress);
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;
    } else {
      this.panOffsetX = this.panTargetX;
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;

      this.isPanningBySpeed = false;
    }
  }

  protected accelerateUpY(calculatedYTopSpeed: number, delta: number) {
    this.speedY = Phaser.Math.Clamp(this.speedY - this.accelerationYPerMS * delta, -calculatedYTopSpeed, calculatedYTopSpeed);
  }

  protected accelerateDownY(calculatedYTopSpeed: number, delta: number) {
    this.speedY = Phaser.Math.Clamp(this.speedY + this.accelerationYPerMS * delta, -calculatedYTopSpeed, calculatedYTopSpeed);
  }

  protected decelerateY(distanceToTargetY: number, decelerationSpeed: number, delta: number) {
    if (this.speedY > decelerationSpeed) this.speedY -= decelerationSpeed;
    else if (this.speedY < -decelerationSpeed) this.speedY += decelerationSpeed;
    else this.speedY = distanceToTargetY;
    // else this.speedY = 0;
  }

  protected moveToPoint(distanceToTargetY: number, decelerationSpeed: number, calculatedYTopSpeed: number, delta: number) {
    // hasExactTarget = true;
    let decelerationDistance = (this.speedY * this.speedY) / (2 * decelerationSpeed);

    if (distanceToTargetY > decelerationDistance) this.accelerateDownY(calculatedYTopSpeed, delta);
    else if (distanceToTargetY < -decelerationDistance) this.accelerateUpY(calculatedYTopSpeed, delta);
    else this.decelerateY(distanceToTargetY, decelerationSpeed, delta);

    // if (distanceToTargetY > this.verticalSpeedPerMS) {
    //   let progress = this.verticalSpeedPerMS / distanceToTargetY;
    //   moveSpeedY = Phaser.Math.Interpolation.Linear([this.y, this.targetY], progress);
    // } else {
    //   moveSpeedY = this.targetY;
    // }
  }

  protected calculateSpeed(time: number, delta: number) {
    let calculatedTopSpeed = this.topSpeedXPerMS * delta;
    let calculatedMaxSpeed = this.maxSpeedPerMS * delta;
    let calculatedAcceleration = this.isAccelerating ? this.accelerationXPerMS : -this.decelerationXPerMS;

    let calculatedYTopSpeed = this.topSpeedYPerMS * delta;

    // X
    this.normalSpeed = Phaser.Math.Clamp(this.normalSpeed + calculatedAcceleration * delta - this.drag, 0, calculatedTopSpeed);
    this.drag = 0;

    if ((this.isStuckInTraffic || this.isBlockedByObstacles) && this.normalSpeed > this.speedLimitPerMS * delta)
      this.normalSpeed = this.speedLimitPerMS * delta;

    let moveSpeedX = Math.min(
      Math.min(this.normalSpeed + this.boostSpeed * delta, calculatedMaxSpeed),
      (this.isStuckInTraffic || this.isBlockedByObstacles ? this.speedLimitPerMS : calculatedMaxSpeed) * delta
    );

    if (moveSpeedX < this.minSpeedPerMS) moveSpeedX = 0;

    // Y
    if (this.areControlsInverted) {
      this.outOfControlTimer -= delta;

      // if (this.outOfControlTimer <= 0) this.isOutOfControl = false;

      if (this.outOfControlTimer > 0) {
        let temp = this.isMovingUp;
        this.isMovingUp = this.isMovingDown;
        this.isMovingDown = temp;
      } else {
        this.isOutOfControl = false;
        this.onOutOfControlEnd();
      }
    }

    if (this.isOutOfControl) {
      if (this.roadY <= this.swervePoints[this.swervePointIndex] + 5 && this.roadY >= this.swervePoints[this.swervePointIndex] - 5) {
        this.swervePointIndex++;
      }

      if (this.swervePointIndex < this.swervePoints.length) {
        this.targetY = this.swervePoints[this.swervePointIndex];
        this.hasTargetY = true;
      } else {
        this.isOutOfControl = false;
        this.onOutOfControlEnd();
      }
    }

    let distanceToTargetY = this.targetY - this.roadY;
    // this.verticalSpeedPerMS = Phaser.Math.Clamp(this.verticalSpeedPerMS + vertAcceleration * delta, 0, calculatedVertTopSpeed);

    // let moveSpeedY = this.y;

    let decelerationSpeed = this.decelerationYPerMS * delta;
    // let hasExactTarget = false;

    if (this.hasTargetY && Math.abs(distanceToTargetY) > 1) {
      this.moveToPoint(distanceToTargetY, decelerationSpeed, calculatedYTopSpeed, delta);
    } else if (this.isMovingUp && !this.isMovingDown) {
      this.hasTargetY = false;
      this.accelerateUpY(calculatedYTopSpeed, delta);
      if (this.isOutOfControl && this.IsPlayer) console.log('Moving up');
    } else if (this.isMovingDown && !this.isMovingUp) {
      this.hasTargetY = false;
      this.accelerateDownY(calculatedYTopSpeed, delta);
      if (this.isOutOfControl && this.IsPlayer) console.log('Moving down');
    } else if (Math.abs(distanceToTargetY) > 1) {
      this.moveToPoint(distanceToTargetY, decelerationSpeed, calculatedYTopSpeed, delta);
    } else {
      this.decelerateY(distanceToTargetY, decelerationSpeed, delta);
    }

    // If the distance to the target is smaller than our acceleration or deceleration then we need to set it explicitly.
    // if (distanceToTargetY <= 1 && this.speedYPerMS == 0) {
    //   this.roadY = this.targetY;
    // }

    // Vehicle Jump.
    let jumpDiff = this.jumpOffsetY - this.jumpPrevOffsetY;
    this.jumpPrevOffsetY = this.jumpOffsetY;
    // if (this.isJumping || this.isInTheAir || this.isLanding) console.log('JUMP', this.jumpOffsetY, jumpDiff);

    // if (this.trackObjectX) {
    // }

    // if (this.trackObjectY) {
    // }

    let newY = this.roadY + this.speedY;

    let bounds = (this.body as MatterJS.BodyType).bounds;
    if (!this.isJumping && !this.isInTheAir && !this.isLanding) {
      for (const [key, zone] of this.vehicleManager.BlockedZones.entries()) {
        // Is in zone X axis.
        if (bounds.max.x > zone.left && bounds.min.x < zone.right) {
          // Is outside and then inside Y axis.
          if (this.y >= zone.bottom && newY < zone.bottom) newY = zone.bottom;
          else if (this.y <= zone.top && newY > zone.top) newY = zone.top;
        }
      }

      const dist = 2000;
      const realX: number = Math.abs(960 - (this.x - this.scene.cameras.main.scrollX));
      let vol = 0;
      if (realX < dist) vol = (dist - realX) / dist;

      app.audioHandler.setVolume(this.sounds.engineRef, Sounds.Channel.SFX, vol * 0.6);
    }

    // Make sure new position is on road.
    this.roadY = Phaser.Math.Clamp(newY, this.roadDetails.roadStartY, this.roadDetails.roadEndY);

    // If no set target then update target to new position.
    // if (!hasExactTarget) this.targetY = this.roadY;
    if (!this.hasTargetY) this.targetY = this.roadY;
    // if (!this.hasTargetY) console.log("No Target Y");

    // this.scene.matter.body.setVelocity(this.body as MatterJS.BodyType, { x: moveSpeedX, y: moveSpeedY });

    this.CharacterSpine.setDriveSpeed(this.panMovement + moveSpeedX);

    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, {
      x: this.shouldControlX ? this.x + this.panMovement + moveSpeedX : this.x,
      y: this.shouldControlY ? this.roadY + this.jumpOffsetY : this.y
    });
    // if (!this.IsPlayer && this.IsPanning) {
    //   console.log('Move', this.panMovement, moveSpeedX);
    // }

    this.panMovement = 0;
    this.prevMoveSpeedX = moveSpeedX;
    this.prevMoveSpeedXPerMS = moveSpeedX / delta;

    if (this.shouldControlDepth) this.setDepth(this.roadY);
    this.isMovingUp = false;
    this.isMovingDown = false;

    this.updateShadow(this.x, this.roadY, this.roadY);
  }

  protected updateShadow(x: number, y: number, depth: number) {
    if (!this.shadow) return;

    this.shadow.x = x + this.shadowData.offsetX;
    // if (!this.isJumping && !this.isInTheAir && !this.isLanding) this.shadow.y = y + this.shadowData.offsetY;
    this.shadow.y = y + this.shadowData.offsetY;

    if (!this.isJumping && !this.isInTheAir) this.shadow.setDepth(depth - 1);
    else this.shadow.setDepth(depth - 9999);
  }

  protected startEngineSFX(sndName: string) {
    this.stopEngineSFX();

    if (this.sounds.engineRef == '') {
      let success;
      [this.sounds.engineRef, success] = app.audioHandler.playSound(this.scene, sndName, Sounds.Channel.SFX, {
        volume: 0.0,
        loop: true
      });
      console.log('SFX ', this.sounds.engineRef);
    }
  }

  public stopEngineSFX() {
    if (this.sounds.engineRef != '') {
      app.audioHandler.stopAndRemoveSound(this.sounds.engineRef, Sounds.Channel.SFX);
      this.sounds.engineRef = '';
    }
  }

  public triggerWeaponCollision(weapon: VehicleEquipmentMeleeWeapon) {
    this.weaponCollision(weapon);
  }

  protected playPlayerSound(snd: string, vol: number = 1.0) {
    if (this.IsPlayer) {
      app.audioHandler.playSound(this.scene, snd, Sounds.Channel.SFX, {
        volume: vol
      });
    }
  }

  // protected addScore(score: number) {
  //   this.pointsScored += score;

  //   if (this.IsPlayer) this.scene.registry.inc(GameDataHandler.keys.Score, score);
  // }

  // protected addDistance(distance: number) {
  //   if (this.IsPlayer) this.scene.registry.inc(GameDataHandler.keys.Distance, distance);
  // }

  protected injectMatter(target: Phaser.GameObjects.GameObject) {
    let x = this.x;
    let y = this.y;

    let matterSprite = this.scene.matter.add.gameObject(target) as Phaser.Physics.Matter.Sprite;

    let vehicleBounds = Vehicle.skinsToSize.get(this.CurrentSkin);

    let offsetX = vehicleBounds?.x ?? 0;
    let offsetY = vehicleBounds?.y ?? 0;
    let width: number = vehicleBounds?.width ?? this.character.displayWidth;
    let height = vehicleBounds?.height ?? this.roadDetails.laneHeight - 40;

    this.vehicleBody = this.scene.matter.bodies.rectangle(offsetX, offsetY, width, height, {
      label: BodyTypes.Vehicle,
      onCollideCallback: this.onOwnVehicleCollisionStart.bind(this),
      onCollideEndCallback: this.onOwnVehicleCollisionEnd.bind(this),
      isSensor: true
    });

    let compoundBody = this.scene.matter.body.create({
      label: BodyTypes.Vehicle,
      parts: [this.vehicleBody],
      restitution: 0,
      inertia: Infinity,
      collisionFilter: {
        group: 0,
        category: 1,
        mask: 0xfffffffd
      }
    });

    // compoundBody.position.x -= 20;
    // compoundBody.position.x = offsetX;
    compoundBody.position.x = 0;
    compoundBody.position.y = this.character.displayOriginY * this.character.scaleY * 0.5 - 30;
    // if (this.CurrentSkin == VehicleSkins.CarGreen) compoundBody.position.y -= 15;

    // this.vehicleBody.position.x = offsetX;
    // this.vehicleBody.position.y = offsetY;

    matterSprite.setExistingBody(compoundBody);

    this.scene.matter.body.setPosition(this.body as MatterJS.BodyType, this.scene.matter.vector.create(x, y));
    this.scene.matter.setVelocity(this.body as MatterJS.BodyType, 0, 0);
  }

  protected onOwnVehicleCollisionStart(pair: MatterJS.ICollisionPair) {
    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Weapon) {
      this.onWeaponCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Weapon) {
      this.onWeaponCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }

    if (
      (pair.collision.normal.x == 1 || pair.collision.normal.x == -1) &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.RampGuide
    ) {
      this.onRampGuideCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if (
      (pair.collision.normal.x == 1 || pair.collision.normal.x == -1) &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.RampGuide
    ) {
      this.onRampGuideCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }

    if (
      (pair.collision.normal.x == 1 || pair.collision.normal.x == -1) &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Ramp
    ) {
      this.onLevelFeatureCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if (
      (pair.collision.normal.x == 1 || pair.collision.normal.x == -1) &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Ramp
    ) {
      this.onLevelFeatureCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }

    // if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Boost) {
    //   this.onBoostCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    // } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Boost) {
    //   this.onBoostCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    // }

    if ((pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Obstacle) {
      this.onObstacleCollisionStart(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if ((pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle && (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Obstacle) {
      this.onObstacleCollisionStart(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onOwnVehicleCollisionEnd(pair: MatterJS.ICollisionPair) {
    if (
      (pair.collision.normal.x == 1 || pair.collision.normal.x == -1) &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.Vehicle &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.RampGuide
    ) {
      this.onRampGuideCollisionEnd(pair, pair.bodyA as MatterJS.BodyType, pair.bodyB as MatterJS.BodyType);
    } else if (
      (pair.collision.normal.x == 1 || pair.collision.normal.x == -1) &&
      (pair.bodyB as MatterJS.BodyType).label == BodyTypes.Vehicle &&
      (pair.bodyA as MatterJS.BodyType).label == BodyTypes.RampGuide
    ) {
      this.onRampGuideCollisionEnd(pair, pair.bodyB as MatterJS.BodyType, pair.bodyA as MatterJS.BodyType);
    }
  }

  protected onWeaponCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, weapon: MatterJS.BodyType) {
    if (vehicle.gameObject instanceof Vehicle && vehicle.gameObject.IsPlayer) {
      if (weapon.gameObject instanceof VehicleEquipmentMeleeWeapon) {
        this.weaponCollision(weapon.gameObject);
      }
    }
  }

  protected weaponCollision(weapon: VehicleEquipmentMeleeWeapon) {
    switch (weapon.EquipmentType) {
      case EquipmentType.BoxingGlove:
      case EquipmentType.GiantMallet:
        this.onHitBackStart(weapon);
        break;
      default:
        this.onHitBackStart(weapon);
        break;
    }
  }

  protected onRampGuideCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, rampGuide: MatterJS.BodyType) {
    if (this.isJumping || this.isInTheAir || this.isLanding) return;

    // if(this.x < rampGuide.position.x)
    // {
    console.log('RAMP GUIDE COLLISION ', this.x, rampGuide.position.x);
    if (rampGuide.gameObject instanceof RampGuide) {
      this.isLocked = true;
      this.moveToY(rampGuide.gameObject.LevelFeature.getRampEntry(), true);
    }
    // }
  }

  protected onRampGuideCollisionEnd(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, rampGuide: MatterJS.BodyType) {
    console.log('ramp collision exit');
    if (this.isJumping || this.isInTheAir || this.isLanding) return;

    // if(this.x < rampGuide.position.x)
    // {
    console.log('RAMP GUIDE COLLISION ', this.x, rampGuide.position.x);
    if (rampGuide.gameObject instanceof RampGuide) {
      this.isLocked = false;
    }
    // }
  }

  protected onLevelFeatureCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, levelFeature: MatterJS.BodyType) {
    if (this.isJumping || this.isInTheAir || this.isLanding) return;

    if (levelFeature.gameObject instanceof RampFeature) {
      this.jump(levelFeature.gameObject);
    } else if (levelFeature.gameObject instanceof RingFeature) {
      this.ring(levelFeature.gameObject);
    }
  }

  // protected onBoostCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, boostEntity: MatterJS.BodyType) {
  //   if (vehicle.gameObject instanceof Vehicle && boostEntity.gameObject instanceof BoostPadAnimated) {
  //     // vehicle.gameObject.boost(boostEntity.gameObject.boostSpeed);
  //   }
  // }

  protected onObstacleCollisionStart(pair: MatterJS.ICollisionPair, vehicle: MatterJS.BodyType, obstacleEntity: MatterJS.BodyType) {
    if (vehicle.gameObject instanceof Vehicle && vehicle.gameObject.IsPlayer && obstacleEntity.gameObject instanceof ObstacleSpine) {
      if (!obstacleEntity.gameObject.HasBeenHit) {
        obstacleEntity.gameObject.onHit(vehicle.gameObject.IsPlayer, vehicle.bounds.max.x, 0);
        vehicle.gameObject.onSwerveStart();
        this.CharacterSpine.hitRecoil();
        app.audioHandler.playSound(this.scene, this.sounds.hit, Sounds.Channel.SFX, { volume: 1 });
        app.audioHandler.playSound(this.scene, this.sounds.whoa, Sounds.Channel.SFX, { volume: 1 });
      }
    }
  }
}
